import React from 'react';

interface ChipMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface MemberChipsProps {
  members: ChipMember[];
  /** Selected member ids, as strings. */
  selected: string[];
  /** The lead's id; that chip is marked and reads as fixed. */
  leadId?: string;
  onToggle: (memberId: string) => void;
  disabled?: boolean;
}

const initialsOf = (m: ChipMember) =>
  `${m.first_name?.charAt(0) ?? ''}${m.last_name?.charAt(0) ?? ''}`.toUpperCase() || '?';

/**
 * Team member picker for the create modals: one chip per member, pressed
 * when selected. Styles live in sass/form-ui.scss (.member-chip), shared with
 * anything else that lists people as chips.
 */
const MemberChips: React.FC<MemberChipsProps> = ({ members, selected, leadId, onToggle, disabled = false }) => (
  <div className="member-chips" role="group" aria-label="Team members">
    {members.map((member) => {
      const id = member.id.toString();
      const isSelected = selected.includes(id);
      const isLead = !!leadId && leadId === id;
      return (
        <button
          key={id}
          type="button"
          className={`member-chip ${isSelected ? 'is-selected' : ''} ${isLead ? 'is-lead' : ''}`.trim()}
          aria-pressed={isSelected}
          onClick={() => onToggle(id)}
          disabled={disabled}
          title={isLead ? 'Project lead' : undefined}
        >
          <span className="member-chip__initials" aria-hidden="true">{initialsOf(member)}</span>
          <span>{member.first_name} {member.last_name}</span>
          {isLead && <span className="member-chip__tag">Lead</span>}
        </button>
      );
    })}
  </div>
);

export default MemberChips;
